import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91蜜桃',
    url: 'https://91mitao.com',
    description: '91蜜桃是国内领先的在线内容平台，致力于为用户提供丰富优质的视频内容和流畅稳定的访问体验。',
    foundingDate: '2021',
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutData.contact.email,
      contactType: 'customer support',
      hoursAvailable: {
        '@type': 'OpeningHoursSpecification',
        hoursAvailable: 'Mo-Su 09:00-21:00',
      },
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于91蜜桃 - 品牌介绍与联系方式',
    description: '了解91蜜桃平台品牌故事、发展历程、核心价值及联系方式。',
    url: 'https://91mitao.com/about',
    about: {
      '@type': 'Organization',
      name: '91蜜桃',
    },
  };

  const faqContactSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '如何联系91蜜桃客服？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以通过邮箱 ${aboutData.contact.email} 联系我们，客服团队将在${aboutData.contact.responseTime}。`,
        },
      },
      {
        '@type': 'Question',
        name: '91蜜桃的客服工作时间是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `客服工作时间为${aboutData.contact.workingHours}。`,
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于91蜜桃 - 品牌介绍与联系方式 | 91蜜桃官方导航"
        description="了解91蜜桃平台品牌故事、发展历程、核心价值理念以及联系方式。91蜜桃致力于为用户提供安全稳定的内容访问服务。"
        keywords="91蜜桃,关于91蜜桃,91蜜桃品牌,蜜桃视频介绍,联系我们"
        canonicalUrl="https://91mitao.com/about"
      />
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqContactSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        {/* Header */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
              <div className="mt-4">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  关于我们
                </h1>
                <p className="mt-2 text-sm text-foreground-600 max-w-2xl">
                  了解91蜜桃的品牌故事与发展历程
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-10 md:py-16">
          <div className="px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  品牌介绍
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  {aboutData.tagline}
                </p>
              </div>
              <div className="max-w-3xl mx-auto space-y-4">
                {aboutData.introduction.map((para, index) => (
                  <p key={index} className="text-sm text-foreground-700 leading-relaxed">
                    {para}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-10 md:py-16 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  核心价值观
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  驱动我们不断前行的核心理念
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutData.values.map((value) => (
                  <div key={value.title} className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-accent-100 flex items-center justify-center">
                      <i className={`${value.icon} text-xl text-accent-600`}></i>
                    </div>
                    <h3 className="font-semibold text-sm text-foreground-900 mb-2">
                      {value.title}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-10 md:py-16">
          <div className="px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  平台声誉
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  数据见证我们的成长与用户认可
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {aboutData.reputation.map((item) => (
                  <div key={item.label} className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-accent-600">
                      {item.stat}
                    </div>
                    <div className="text-xs text-foreground-500 mt-2">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-10 md:py-16 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  发展历程
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  一路走来的重要里程碑
                </p>
              </div>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 transform md:-translate-x-px"></div>
                <div className="space-y-8">
                  {aboutData.timeline.map((item, index) => (
                    <div key={item.year} className={`relative flex items-start gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 transform -translate-x-1/2 mt-1.5 z-10"></div>
                      <div className={`ml-10 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                        <span className="inline-block text-xs font-bold text-accent-600 bg-accent-100 px-2 py-0.5 rounded mb-2 whitespace-nowrap">
                          {item.year}
                        </span>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                          {item.title}
                        </h3>
                        <p className="text-xs text-foreground-600 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                      <div className="hidden md:block md:w-1/2"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-10 md:py-16">
          <div className="px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  联系我们
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  如有任何问题或建议，欢迎随时与我们取得联系
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-600">{aboutData.contact.email}</p>
                  <p className="text-xs text-foreground-400 mt-1">{aboutData.contact.responseTime}</p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-time-line text-lg text-accent-600"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">服务时间</h3>
                  <p className="text-xs text-foreground-600">{aboutData.contact.workingHours}</p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-secondary-100 flex items-center justify-center">
                    <i className="ri-chat-smile-2-line text-lg text-secondary-600"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">在线反馈</h3>
                  <p className="text-xs text-foreground-600">使用下方表单提交您的反馈</p>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6 text-center">
                  在线留言
                </h3>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

function ContactForm() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const formUrl = 'https://readdy.ai/api/form/d9pit1hn2trhb35gvgdg';

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormState('success');
      return;
    }
    formData.delete('website_alt');

    setFormState('submitting');
    setFormError('');

    try {
      const searchParams = new URLSearchParams();
      formData.forEach((value, key) => {
        searchParams.append(key, value as string);
      });

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: searchParams.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch (_) {
        /* ignore parse errors */
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        setFormState('error');
        setFormError(serverMsg);
      }
    } catch (_err) {
      setFormState('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  }, [formUrl]);

  return (
    <>
      {formState === 'success' ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <i className="ri-check-line text-xl text-green-600"></i>
          </div>
          <h4 className="text-sm font-semibold text-foreground-900 mb-1">提交成功</h4>
          <p className="text-xs text-foreground-500">感谢您的留言，我们会尽快回复您！</p>
        </div>
      ) : (
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="spam-field"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                您的姓名 <span className="text-primary-500">*</span>
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                placeholder="请输入您的姓名"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                电子邮箱 <span className="text-primary-500">*</span>
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                placeholder="请输入您的邮箱"
              />
            </div>
          </div>
          <div>
            <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
              主题 <span className="text-primary-500">*</span>
            </label>
            <input
              id="contact-subject"
              name="subject"
              type="text"
              required
              className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
              placeholder="请输入留言主题"
            />
          </div>
          <div>
            <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
              留言内容 <span className="text-primary-500">*</span>
            </label>
            <textarea
              id="contact-message"
              name="message"
              rows={4}
              required
              maxLength={500}
              className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
              placeholder="请输入您的留言内容（最多500字）"
            ></textarea>
          </div>
          {formState === 'error' && formError && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {formError}
            </div>
          )}
          <button
            type="submit"
            disabled={formState === 'submitting'}
            className="w-full px-6 py-2.5 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm whitespace-nowrap cursor-pointer"
          >
            {formState === 'submitting' ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                提交中...
              </>
            ) : (
              <>
                <i className="ri-send-plane-line mr-2"></i>
                提交留言
              </>
            )}
          </button>
        </form>
      )}
    </>
  );
}